import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import {
  Add as AddIcon,
  Save as SaveIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import BookingIncomeSummary from "./BookingIncomeSummary";
import { incomeValidationSchema } from "./validationSchema";
import { IncomeDetailsData, IncomeDetailsProps } from "./types";
import { calculateIncomeSummary } from "./utils/calculateIncomeSummary";
import { bookingApi } from "./apiService";
import { Loading } from "../../components/Loading";
import FixedIncome from "./components/FixedIncome";
import OtherIncomes from "./components/OtherIncomes";
import { calculateAmount } from "./utils/calculateAmounts";
import { useToast } from "../../components/Toast";
import { commonTextFieldProps, smallButtonProp } from "../../utils/styles";
import PaymentDetails from "./components/PaymentDetails";
import { formatCurrency } from "../../utils/utils";
import DeletedPayments from "./components/DeletedPayments";
import DeletedIncomeDetails from "./components/DeletedIncomeDetails";
import GSTView from "../../components/GSTView";

const formatInputCurrency = (
  value: string | number | undefined,
  fraction: number
) => {
  if (!value && value !== 0) return "0";
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(numValue);
};

const IncomeDetails = ({
  incomeSummary,
  setIncomeSummary,
  rentalJourneyId,
}: IncomeDetailsProps) => {
  const [deletedIncomeDetails, setDeletedIncomeDetails] = useState<any>([]);
  const [showChangeLogs, setShowChangeLogs] = useState<boolean>(false);
  const [deletedPaymentDetails, setDeletedPaymentDetails] = useState([]);
  const [showPaymentChangeLogs, setShowPaymentChangeLogs] =
    useState<boolean>(false);
  const [incomeDetails, setIncomeDetails] = useState<any>([
    {
      incomeType: "perkm",
      rentalJourneyId: rentalJourneyId,
      amount: 0,
      limit: 0,
      overLimitRate: 0,
      gstPercentage: 0,
      remarks: "",
      paymentDetails: [],
      isNew: true,
    },
  ]);
  const [additionalFields, setAdditionalFields] = useState({
    sgstAmount: 0,
    cgstAmount: 0,
    totalAmount: 0,
    totalAmountExclGst: 0,
    balanceAmount: 0,
    fixedOrPerKmIncomeExclGst: 0,
    fixedOrPerKmIncomeInclGst: 0,
    fixedOrPerKmIncomeGst: 0,
  });
  const [globalGstPercentage, setGlobalGstPercentage] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [originalIncomeDetails, setOriginalIncomeDetails] = useState<any>([]);
  const [originalPaymentDetails, setOriginalPaymentDetails] = useState<any>([]);
  const { showToast } = useToast();

  const checkDisabled = () => {
    // You can customize this logic based on your requirements
    // For now, returning false to allow editing
    return false;
  };

  const fetchIncomeDetails = async (rentalJourneyId: string) => {
    setIsLoading(true);
    const response = await bookingApi.getIncomeDetails(rentalJourneyId);
    if (response?.rentalIncome?.length > 0) {
      setIncomeDetails(response?.rentalIncome);
      setOriginalIncomeDetails(response?.rentalIncome);
      setOriginalPaymentDetails(
        response?.rentalIncome?.flatMap((i: any) => i?.paymentDetails || [])
      );

      // Set global GST from the first income that has GST
      const incomeWithGst = response?.rentalIncome.find(
        (i: any) => i?.gstPercentage && i?.gstPercentage > 0
      );
      if (incomeWithGst) {
        setGlobalGstPercentage(incomeWithGst.gstPercentage);
      }

      const income = response?.rentalIncome.find((i: any) =>
        ["perkm", "fixed"].includes(i?.incomeType)
      );
      if (income) {
        calculateAmount(response?.rentalIncome, setAdditionalFields);
        calculateIncomeSummary(response?.rentalIncome, setIncomeSummary);
      }
    }

    setDeletedIncomeDetails(response?.deletedRentalIncome);
    setDeletedPaymentDetails(response?.deletedRentalIncomePayments);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchIncomeDetails(rentalJourneyId);
  }, []);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (originalIncomeDetails?.length === 0) {
        const response = await bookingApi.createIncomeDetails(values?.incomes);
        if (response) {
          showToast("Income details created successfully!", "success");
          fetchIncomeDetails(rentalJourneyId);
          setIsSubmitting(false);
        }
      } else {
        let incomesToCreate: any[] = [];
        let incomesToUpdate: any[] = [];
        let incomesToDelete: any[] = [];

        await Promise.all(
          values?.incomes?.map(async (income: any) => {
            if (income?.isEdited && income?.id) {
              incomesToUpdate.push(income);
              const originalIncome = originalIncomeDetails?.find(
                (i: any) => i?.id === income?.id
              );
              if (originalIncome) {
                const { id: _, ...dataToCreate } = originalIncome;
                incomesToDelete.push({
                  ...dataToCreate,
                  deletedAt: new Date(),
                });
              }
            } else if (income?.isNew) {
              incomesToCreate.push(income);
            } else if (income?.deletedAt) {
              incomesToDelete.push(income);
            }
          })
        );

        const paymentsToSoftDelete: any[] = [];
        const paymentsToCreate: any[] = [];
        const paymentsToUpdate: any[] = [];

        await Promise.all(
          values?.incomes?.map(async (income: any) => {
            if (income?.paymentDetails) {
              income?.paymentDetails?.forEach((payment: any) => {
                if (payment?.deletedAt && payment?.id) {
                  paymentsToSoftDelete.push(payment);
                } else if (
                  payment?.isNew &&
                  !payment?.id &&
                  payment?.rentalJourneyIncomeId
                ) {
                  paymentsToCreate.push(payment);
                } else if (payment?.id && payment?.isEdited) {
                  paymentsToUpdate.push(payment);
                  const originalPayment = originalPaymentDetails.find(
                    (p: any) => p?.id === payment?.id
                  );
                  if (originalPayment) {
                    const { id: _, ...dataToCreate } = originalPayment;
                    paymentsToCreate.push({
                      ...dataToCreate,
                      deletedAt: new Date(),
                    });
                  }
                }
              });
            }
          })
        );

        let promises: Promise<any>[] = [];

        if (incomesToCreate?.length > 0) {
          promises.push(bookingApi.createIncomeDetails(incomesToCreate));
        }
        if (incomesToUpdate?.length > 0 || incomesToDelete?.length > 0) {
          promises.push(
            bookingApi.updateIncomeDetails(rentalJourneyId, incomesToUpdate)
          );
          promises.push(bookingApi.deleteIncomeDetails(incomesToDelete));
        }
        if (paymentsToCreate?.length > 0) {
          promises.push(bookingApi.createIncomePayment(paymentsToCreate));
        }
        if (paymentsToUpdate?.length > 0) {
          promises.push(bookingApi.updateBulkIncomePayment(paymentsToUpdate));
        }

        // Add soft delete for payments with deletedAt flag
        if (paymentsToSoftDelete?.length > 0) {
          const paymentIds = paymentsToSoftDelete.map((p) => p.id);
          promises.push(
            bookingApi.softDeleteIncomePayment({ ids: paymentIds })
          );
        }

        if (promises?.length > 0) {
          const response = await Promise.all(promises);
          showToast("Income details updated successfully!", "success");
          fetchIncomeDetails(rentalJourneyId);
          setIsSubmitting(false);
        } else {
          showToast("No changes to save", "info");
          setIsSubmitting(false);
        }
      }
    } catch (error: any) {
      showToast();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentChange = (
    values: IncomeDetailsData,
    field: string,
    value: any,
    idx: number,
    setFieldValue: any
  ) => {
    const [parent, child] = field.split(".");
    if (parent === "paymentDetails") {
      const updatedPayment: any = [...(values?.paymentDetails || [])];
      updatedPayment[idx][child] = value;
      updatedPayment[idx].isEdited = true;
      setFieldValue("paymentDetails", updatedPayment);
    }
  };

  if (isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={true} />
    ) : (
      <Loading message="Loading..." fullScreen={true} />
    );
  }

  return (
    <>
      <Formik
        initialValues={{ incomes: incomeDetails }}
        validationSchema={incomeValidationSchema}
        onSubmit={(values) => handleSubmit(values)}
        enableReinitialize
      >
        {({ values, errors, setFieldValue, dirty }) => {
          return (
            <Form>
              <BookingIncomeSummary data={incomeSummary} />
              <Paper elevation={3} sx={{ p: 2 }}>
                <FieldArray name="incomes">
                  {({ remove }) => (
                    <>
                      {values.incomes?.map((income: any, index: number) => (
                        <FixedIncome
                          income={income}
                          index={index}
                          values={values}
                          setFieldValue={setFieldValue}
                          setIncomeSummary={setIncomeSummary}
                          additionalFields={additionalFields}
                          setAdditionalFields={setAdditionalFields}
                          formatCurrency={formatInputCurrency}
                          calculateIncomeSummary={calculateIncomeSummary}
                          errors={errors}
                          gstPercentage={globalGstPercentage}
                        />
                      ))}
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: "0.9rem", mb: 1 }}
                      >
                        Other Incomes
                      </Typography>
                      {values?.incomes?.map((income: any, index: number) => (
                        <OtherIncomes
                          income={income}
                          index={index}
                          values={values}
                          setFieldValue={setFieldValue}
                          setIncomeSummary={setIncomeSummary}
                          setAdditionalFields={setAdditionalFields}
                          formatCurrency={formatInputCurrency}
                          remove={remove}
                          calculateIncomeSummary={calculateIncomeSummary}
                          errors={errors}
                          gstPercentage={globalGstPercentage}
                        />
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const income = {
                            rentalJourneyId: rentalJourneyId,
                            incomeType: "",
                            amount: 0,
                            limit: null,
                            perKmRate: null,
                            overLimitRate: null,
                            gstPercentage: null,
                            remarks: null,
                            paymentDetails: [],
                            isNew: true,
                          };
                          setFieldValue("incomes", [
                            ...values?.incomes,
                            income,
                          ]);
                        }}
                        size="small"
                        sx={{ ...smallButtonProp?.sx, mt: 1 }}
                      >
                        Add Income
                      </Button>
                      {additionalFields?.totalAmount > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontSize: "0.9rem", mb: 1 }}
                          >
                            GST Details
                          </Typography>
                          <Box
                            sx={{
                              p: 1,
                              pt: 2,
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "grey.300",
                              bgcolor: "#fdfdfd",
                              maxWidth: { sm: "20%", xs: "100%" },
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={12}>
                                <TextField
                                  fullWidth
                                  label="GST %"
                                  value={globalGstPercentage}
                                  onChange={(e) => {
                                    const gstValue =
                                      parseFloat(e.target.value) || 0;
                                    setGlobalGstPercentage(gstValue);
                                    values?.incomes?.forEach(
                                      (_: any, index: number) => {
                                        setFieldValue(
                                          `incomes.${index}.isEdited`,
                                          true
                                        );
                                      }
                                    );
                                  }}
                                  onBlur={() => {
                                    values?.incomes?.length > 0 &&
                                      calculateIncomeSummary(
                                        values?.incomes,
                                        setIncomeSummary
                                      );
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{
                                    backgroundColor: "#fff",
                                    ...commonTextFieldProps.sx,
                                  }}
                                  size="small"
                                  InputProps={{
                                    endAdornment: (
                                      <span
                                        style={{
                                          marginRight: 8,
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        %
                                      </span>
                                    ),
                                  }}
                                  disabled={checkDisabled()}
                                />
                              </Grid>
                              {globalGstPercentage > 0 &&
                                incomeSummary?.amountBeforeGst > 0 && (
                                  <>
                                    <Grid item xs={12} md={12}>
                                      <GSTView
                                        amount={
                                          incomeSummary?.amountBeforeGst || 0
                                        }
                                        gstPercentage={globalGstPercentage}
                                      />
                                    </Grid>
                                  </>
                                )}
                            </Grid>
                          </Box>
                        </>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <PaymentDetails
                        values={values?.incomes?.find((income: any) =>
                          ["perkm", "fixed"].includes(income?.incomeType)
                        )}
                        handleChange={handlePaymentChange}
                        setFieldValue={setFieldValue}
                        calculateAmounts={() => {
                          calculateAmount(values?.incomes, setAdditionalFields);
                          values?.incomes?.length > 0 &&
                            calculateIncomeSummary(
                              values?.incomes,
                              setIncomeSummary
                            );
                        }}
                        incomeSummary={incomeSummary}
                        index={values?.incomes?.findIndex((income: any) =>
                          ["perkm", "fixed"].includes(income?.incomeType)
                        )}
                      />
                    </>
                  )}
                </FieldArray>

                {deletedIncomeDetails?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "gray",
                        fontSize: "0.75rem",
                      }}
                      onClick={() => {
                        setShowChangeLogs(!showChangeLogs);
                      }}
                    >
                      Income Change Logs
                      {showChangeLogs ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )}
                    </Typography>
                    {showChangeLogs && (
                      <Box sx={{ mt: 1 }}>
                        {deletedIncomeDetails?.length > 0 && (
                          <DeletedIncomeDetails
                            deletedIncomeDetails={deletedIncomeDetails}
                            incomeType={deletedIncomeDetails?.[0]?.incomeType}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                {deletedPaymentDetails?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "gray",
                        fontSize: "0.75rem",
                      }}
                      onClick={() => {
                        setShowPaymentChangeLogs(!showPaymentChangeLogs);
                      }}
                    >
                      Payment Change Logs
                      {showPaymentChangeLogs ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )}
                    </Typography>
                    {showPaymentChangeLogs && (
                      <Box sx={{ mt: 1 }}>
                        {deletedPaymentDetails?.length > 0 && (
                          <DeletedPayments
                            deletedPaymentDetails={deletedPaymentDetails}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    p: 2,
                    mt: 2,
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2">Balance Remaining</Typography>
                  <Typography variant="subtitle2" sx={{ fontStyle: "italic" }}>
                    {formatCurrency(incomeSummary?.balanceAmount || 0)}
                  </Typography>
                </Box>
              </Paper>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  type="submit"
                  disabled={isSubmitting || !dirty}
                  sx={{ minWidth: 120, height: 30, fontSize: "0.7rem" }}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default IncomeDetails;
